import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { useLanguage, useLangPrefix } from "@/i18n/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const lang = useLanguage();
  const prefix = useLangPrefix();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const navItems =
    lang === "en"
      ? [
          { to: `${prefix}`, label: "Home" },
          { to: `${prefix}/blind-installation`, label: "Blinds" },
          { to: `${prefix}/balcony-installation`, label: "Balcony" },
          { to: `${prefix}/roller-blind-installation`, label: "Roller blinds" },
          { to: `${prefix}/vertical-blind-installation`, label: "Vertical blinds" },
          { to: `${prefix}/curtain-track-installation`, label: "Curtain tracks" },
        ]
      : [
          { to: "/", label: "Etusivu" },
          { to: "/kaihdin-asennus", label: "Kaihtimet" },
          { to: "/parveke-asennus", label: "Parvekkeet" },
          { to: "/rullaverho-asennus", label: "Rullaverhot" },
          { to: "/lamelliverho-asennus", label: "Lamelliverhot" },
          { to: "/verhokisko-asennus", label: "Verhokiskot" },
        ];

  const isActive = (path: string) => {
    if (path === "/" || path === "/en") {
      return location.pathname === path;
    }
    return location.pathname === path;
  };

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[60] focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded-md"
      >
        {lang === "en" ? "Skip to content" : "Siirry sisältöön"}
      </a>

      {/* Floating navbar */}
      <header
        className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 transition-all duration-500 ${
          scrolled ? "top-3" : "top-5"
        }`}
      >
        <nav
          className={`flex items-center gap-1 px-2 py-1.5 rounded-full border transition-all duration-500 ${
            scrolled
              ? "bg-background/90 backdrop-blur-xl shadow-lg border-border/60"
              : "bg-background/70 backdrop-blur-md shadow-md border-border/30"
          }`}
          aria-label={lang === "en" ? "Main navigation" : "Päänavigaatio"}
        >
          {/* Logo – desktop only */}
          <Link
            to={prefix || "/"}
            className="hidden lg:flex items-center font-hero text-sm font-bold tracking-tight text-primary px-3 py-1.5 whitespace-nowrap"
          >
            KU Kaihdin
          </Link>

          {/* Separator – desktop only */}
          <div className="hidden lg:block w-px h-5 bg-border/60 mx-1" />

          {/* Desktop nav links */}
          <div className="hidden lg:flex items-center gap-0.5">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`relative px-3.5 py-1.5 text-[13px] font-medium rounded-full transition-colors whitespace-nowrap ${
                  isActive(item.to)
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {isActive(item.to) && (
                  <motion.span
                    layoutId="nav-bubble"
                    className="absolute inset-0 rounded-full bg-muted border border-border/50"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
                <span className="relative z-10">{item.label}</span>
              </Link>
            ))}
          </div>

          {/* Separator – desktop only */}
          <div className="hidden lg:block w-px h-5 bg-border/60 mx-1" />

          {/* Phone CTA – desktop */}
          <a
            href="tel:0405839991"
            className="hidden lg:inline-flex items-center gap-1.5 bg-primary text-primary-foreground px-4 py-1.5 rounded-full text-[13px] font-semibold hover:opacity-90 transition-opacity whitespace-nowrap"
          >
            <Phone className="w-3.5 h-3.5" />
            040 583 9991
          </a>

          {/* Mobile: logo + hamburger */}
          <Link
            to={prefix || "/"}
            className="lg:hidden font-hero text-sm font-bold tracking-tight text-primary px-3 py-1.5 whitespace-nowrap"
          >
            KU Kaihdin
          </Link>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 rounded-full hover:bg-muted transition-colors"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </nav>

        {/* Mobile dropdown */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.97 }}
              transition={{ duration: 0.2 }}
              className="mt-2 rounded-2xl bg-background/95 backdrop-blur-xl border border-border/60 shadow-xl p-3 space-y-1"
            >
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className={`block px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    isActive(item.to)
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              <a
                href="tel:0405839991"
                className="flex items-center justify-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-xl text-sm font-semibold mt-2"
              >
                <Phone className="w-4 h-4" />
                040 583 9991
              </a>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};

export default Header;
